import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif";

const QUESTION_LABELS = {
  q1_experience: { label: "Q1: Dating Experience", options: ["Great", "Good", "Neutral", "Frustrating", "Exhausting"] },
  q2_frustration: { label: "Q2: Biggest Frustration", options: ["Low-quality matches", "Too much time wasted", "Ghosting", "People not serious", "Too many options / overwhelming"] },
  q3_excitement_frequency: { label: "Q3: Meeting Exciting People", options: ["Often", "Sometimes", "Rarely", "Almost never"] },
  q4_quality_preference: { label: "Q4: Prefer Quality Over Quantity", options: ["Yes", "No", "Not sure"] },
  q5_no_swipe_interest: { label: "Q5: Try No-Swipe Experience", options: ["Yes", "No", "Maybe"] },
};

function MetricCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-6" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[#999] text-xs uppercase tracking-wider mb-2" style={{ fontWeight: 500 }}>{label}</p>
      <p className="text-[#111] text-3xl" style={{ fontWeight: 700 }}>{value}</p>
      {sub && <p className="text-[#999] text-sm mt-1">{sub}</p>}
    </div>
  );
}

function DistributionBar({ label, percentage, count, highlight }) {
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className={highlight ? "text-[#111] font-medium" : "text-[#444]"}>{label}</span>
        <span className="text-[#999]">{count} ({percentage}%)</span>
      </div>
      <div className="h-2 bg-[#F0F0F0] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${percentage}%`, backgroundColor: highlight ? "#D89B00" : "#CBD5E1" }}
        />
      </div>
    </div>
  );
}

export default function PulseAdmin() {
  const [summary, setSummary] = useState(null);
  const [responses, setResponses] = useState([]);
  const [totalResponses, setTotalResponses] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    async function fetchData() {
      try {
        const [sumRes, respRes] = await Promise.all([
          fetch(`${API_URL}/api/pulse/admin/summary`),
          fetch(`${API_URL}/api/pulse/admin/responses?limit=50`),
        ]);
        const sumData = await sumRes.json();
        const respData = await respRes.json();
        setSummary(sumData);
        setResponses(respData.responses || []);
        setTotalResponses(respData.total || 0);
      } catch (e) {
        console.error("Failed to fetch pulse admin data", e);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleExport = () => {
    window.open(`${API_URL}/api/pulse/admin/export`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center" style={{ fontFamily: FONT }}>
        <p className="text-[#999] text-lg">Loading Pulse data...</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center" style={{ fontFamily: FONT }}>
        <p className="text-[#999] text-lg">No data available yet.</p>
      </div>
    );
  }

  const negRate = summary.distributions?.q1_experience?.total
    ? (((summary.distributions.q1_experience.options["Frustrating"]?.count || 0) + (summary.distributions.q1_experience.options["Exhausting"]?.count || 0)) / summary.distributions.q1_experience.total * 100).toFixed(1)
    : 0;
  const lowExciteRate = summary.distributions?.q3_excitement_frequency?.total
    ? (((summary.distributions.q3_excitement_frequency.options["Rarely"]?.count || 0) + (summary.distributions.q3_excitement_frequency.options["Almost never"]?.count || 0)) / summary.distributions.q3_excitement_frequency.total * 100).toFixed(1)
    : 0;
  const antiSwipeRate = summary.distributions?.q5_no_swipe_interest?.total
    ? ((summary.distributions.q5_no_swipe_interest.options["Yes"]?.count || 0) / summary.distributions.q5_no_swipe_interest.total * 100).toFixed(1)
    : 0;
  const qualityRate = summary.distributions?.q4_quality_preference?.total
    ? ((summary.distributions.q4_quality_preference.options["Yes"]?.count || 0) / summary.distributions.q4_quality_preference.total * 100).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-[#F8F7F5]" style={{ fontFamily: FONT }} data-testid="pulse-admin">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] px-6 py-5">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-[#111] text-xl" style={{ fontWeight: 700 }}>Pulse Admin</h1>
            <p className="text-[#999] text-sm">Austin Dating Pulse Check</p>
          </div>
          <button
            onClick={handleExport}
            className="text-sm text-white px-5 py-2 rounded-lg"
            style={{ backgroundColor: "#D89B00", fontWeight: 600, cursor: "pointer", border: "none" }}
            data-testid="export-csv-btn"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white rounded-lg p-1 border border-[#E5E7EB] w-fit">
          {["overview", "breakdown", "responses"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-md text-sm capitalize transition-colors ${
                tab === t ? "bg-[#111] text-white" : "text-[#666] hover:bg-[#F0F0F0]"
              }`}
              style={{ fontWeight: 500, cursor: "pointer", border: "none" }}
              data-testid={`tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {tab === "overview" && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <MetricCard label="Total Responses" value={summary.total_responses} />
              <MetricCard label="Completed" value={summary.completed_responses} sub={`${summary.completion_rate}% rate`} />
              <MetricCard label="Email Capture" value={`${summary.email_capture_rate}%`} />
              <MetricCard label="Responses Today" value={responses.filter(r => r.created_at?.startsWith(new Date().toISOString().slice(0, 10))).length} />
            </div>
            <h3 className="text-[#111] text-lg mb-4" style={{ fontWeight: 600 }}>Key Insights</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Negative Experience" value={`${negRate}%`} sub="Frustrating + Exhausting" />
              <MetricCard label="Low Excitement" value={`${lowExciteRate}%`} sub="Rarely + Almost never" />
              <MetricCard label="Anti-Swipe Interest" value={`${antiSwipeRate}%`} sub="Would try no-swiping" />
              <MetricCard label="Quality Preference" value={`${qualityRate}%`} sub="Prefer fewer, better" />
            </div>
          </div>
        )}

        {/* BREAKDOWN TAB */}
        {tab === "breakdown" && (
          <div className="space-y-8">
            {Object.entries(QUESTION_LABELS).map(([field, { label, options }]) => {
              const dist = summary.distributions?.[field];
              if (!dist) return null;
              return (
                <div key={field} className="bg-white rounded-xl border border-[#E5E7EB] p-6" data-testid={`breakdown-${field}`}>
                  <h3 className="text-[#111] text-base mb-1" style={{ fontWeight: 600 }}>{label}</h3>
                  <p className="text-[#999] text-sm mb-5">{dist.total} responses</p>
                  {options.map((opt) => {
                    const d = dist.options[opt] || { count: 0, percentage: 0 };
                    const isNeg = (field === "q1_experience" && ["Frustrating", "Exhausting"].includes(opt)) ||
                                  (field === "q3_excitement_frequency" && ["Rarely", "Almost never"].includes(opt));
                    const isPos = (field === "q4_quality_preference" && opt === "Yes") ||
                                  (field === "q5_no_swipe_interest" && opt === "Yes");
                    return <DistributionBar key={opt} label={opt} percentage={d.percentage} count={d.count} highlight={isNeg || isPos} />;
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* RESPONSES TAB */}
        {tab === "responses" && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto" data-testid="responses-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-left px-4 py-3 text-[#999] font-medium">Time</th>
                  <th className="text-left px-4 py-3 text-[#999] font-medium">Q1</th>
                  <th className="text-left px-4 py-3 text-[#999] font-medium">Q2</th>
                  <th className="text-left px-4 py-3 text-[#999] font-medium">Q3</th>
                  <th className="text-left px-4 py-3 text-[#999] font-medium">Q4</th>
                  <th className="text-left px-4 py-3 text-[#999] font-medium">Q5</th>
                  <th className="text-left px-4 py-3 text-[#999] font-medium">Email</th>
                </tr>
              </thead>
              <tbody>
                {responses.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                    <td className="px-4 py-3 text-[#666] whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                    <td className="px-4 py-3 text-[#222]">{r.q1_experience || "—"}</td>
                    <td className="px-4 py-3 text-[#222]">{r.q2_frustration || "—"}</td>
                    <td className="px-4 py-3 text-[#222]">{r.q3_excitement_frequency || "—"}</td>
                    <td className="px-4 py-3 text-[#222]">{r.q4_quality_preference || "—"}</td>
                    <td className="px-4 py-3 text-[#222]">{r.q5_no_swipe_interest || "—"}</td>
                    <td className="px-4 py-3 text-[#666]">{r.email || "—"}</td>
                  </tr>
                ))}
                {responses.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-[#999]">No responses yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
